<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { call, identity, type Schemas } from '../api/http'
import { useSession } from '../stores/session'
import { compact, useForm } from '../lib/form'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import LegalFields from '../components/LegalFields.vue'

const { t } = useI18n()
const session = useSession()
const load = useForm()
const action = useForm()
const create = useForm()
const showCreate = ref(false)

const emptyLegal = (): Schemas['Legal'] => ({ legal_name: '', reg_number: '', address: '', vat_number: '', iban: '', bank_name: '' })
const form = reactive({ name: '', kind: 'personal' as Schemas['WorkspaceKind'], legal: emptyLegal() })

onMounted(() => load.submit(() => session.loadMe()))

async function onCreate() {
  const body: Schemas['CreateWorkspaceRequest'] = { name: form.name, kind: form.kind }
  if (form.kind === 'business') body.legal = compact(form.legal)
  const ok = await create.submit(async () => {
    await call(identity.POST('/workspaces', { body }))
    await session.loadMe()
  })
  if (ok) {
    Object.assign(form, { name: '', kind: 'personal', legal: emptyLegal() })
    showCreate.value = false
  }
}

const switchTo = (id: string) => action.submit(() => session.switchWorkspace(id))

async function remove(w: Schemas['Workspace']) {
  if (!confirm(t('workspaces.deleteConfirm', { name: w.name }))) return
  await action.submit(async () => {
    await call(identity.DELETE('/workspaces/{workspace_id}', { params: { path: { workspace_id: w.id } } }))
    await session.loadMe()
  })
}
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>{{ $t('workspaces.title') }}</h1>
        <p class="muted">{{ $t('workspaces.subtitle') }}</p>
      </div>
      <UiButton v-if="!showCreate" data-testid="new-workspace" @click="showCreate = true">{{ $t('workspaces.new') }}</UiButton>
    </header>

    <UiCard v-if="showCreate" :title="$t('workspaces.createTitle')">
      <form class="stack" data-testid="create-workspace" @submit.prevent="onCreate">
        <ErrorBanner :error="create.error.value" />
        <UiField :label="$t('fields.workspaceName')" :error="create.fieldErr('name')">
          <input v-model.trim="form.name" name="name" required maxlength="100" :aria-invalid="!!create.fieldErr('name')" />
        </UiField>
        <fieldset class="kinds">
          <legend class="small">{{ $t('fields.kind') }}</legend>
          <label v-for="k in ['personal', 'business'] as const" :key="k" :class="['kind', { on: form.kind === k }]">
            <input v-model="form.kind" type="radio" name="kind" :value="k" />
            <span>
              <strong>{{ $t(`workspace.kind.${k}`) }}</strong>
              <span class="muted small">{{ $t(`workspaces.kindHint.${k}`) }}</span>
            </span>
          </label>
        </fieldset>
        <LegalFields v-if="form.kind === 'business'" v-model="form.legal" :field-err="create.fieldErr" />
        <div class="row">
          <UiButton type="submit" :loading="create.busy.value">{{ $t('common.create') }}</UiButton>
          <UiButton variant="ghost" @click="showCreate = false">{{ $t('common.cancel') }}</UiButton>
        </div>
      </form>
    </UiCard>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <UiCard>
      <p v-if="load.busy.value && !session.workspaces.length" class="muted">{{ $t('common.loading') }}</p>
      <ul v-else class="list" data-testid="workspace-list">
        <li v-for="w in session.workspaces" :key="w.id">
          <div class="stack" style="gap: 0.2rem; flex: 1; min-width: 12rem">
            <strong>{{ w.name }}</strong>
            <div class="row" style="gap: 0.4rem">
              <UiBadge :variant="w.kind === 'business' ? 'default' : 'secondary'">{{ $t(`workspace.kind.${w.kind}`) }}</UiBadge>
              <UiBadge variant="outline">{{ $t(`roles.${w.role}`) }}</UiBadge>
              <UiBadge v-if="w.suspended" variant="danger">{{ $t('workspace.suspended') }}</UiBadge>
            </div>
          </div>
          <UiBadge v-if="w.id === session.workspace?.id" variant="success">{{ $t('workspaces.active') }}</UiBadge>
          <UiButton v-else size="sm" variant="outline" :disabled="w.suspended || action.busy.value" @click="switchTo(w.id)">
            {{ $t('workspaces.switch') }}
          </UiButton>
          <UiButton v-if="w.role === 'owner'" size="sm" variant="ghost" :disabled="action.busy.value" @click="remove(w)">
            {{ $t('common.delete') }}
          </UiButton>
        </li>
      </ul>
    </UiCard>
  </div>
</template>

<style scoped>
.kinds {
  border: 0;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}
.kinds legend {
  font-weight: 500;
  margin-bottom: 0.3rem;
}
.kind {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.75rem;
  cursor: pointer;
}
.kind.on {
  border-color: var(--accent);
  background: rgb(var(--accent-rgb) / 0.05);
}
.kind input {
  width: auto;
  min-height: 0;
  margin-top: 0.3rem;
}
.kind span {
  display: flex;
  flex-direction: column;
}
</style>
